// ─────────────────────────────────────────────
// KLOTHIQ — Partner Create / Edit Modal
// ─────────────────────────────────────────────

import UserGuide from '@/components/help/UserGuide';
import { Input } from '@/components/ui/FormControls';
import { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { usePartners, useUpsertPartner, Partner } from '@/hooks/useAccounts';
import { parseRpcError } from '@/utils/cashbookCalc';

interface PartnerFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  partner?: Partner | null;
}

export default function PartnerFormModal({
  isOpen,
  onClose,
  partner,
}: PartnerFormModalProps) {
  const { data: partners } = usePartners(true);
  const upsertPartner = useUpsertPartner();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [sharePercent, setSharePercent] = useState('0');
  const [isActive, setIsActive] = useState(true);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (partner) {
      setName(partner.name);
      setPhone(partner.phone || '');
      setSharePercent(String(partner.share_percent));
      setIsActive(partner.is_active);
      setNotes(partner.notes || '');
    } else {
      setName('');
      setPhone('');
      setSharePercent('0');
      setIsActive(true);
      setNotes('');
    }
  }, [partner, isOpen]);

  if (!isOpen) return null;

  // Calculate current other partners' share total
  const otherShareTotal = (partners ?? [])
    .filter((p) => p.is_active && (!partner || p.id !== partner.id))
    .reduce((s, p) => s + Number(p.share_percent), 0);

  const currentEnteredShare = parseFloat(sharePercent) || 0;
  const projectedTotal = isActive ? otherShareTotal + currentEnteredShare : otherShareTotal;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Partner name is required');
      return;
    }
    const shareNum = parseFloat(sharePercent);
    if (isNaN(shareNum) || shareNum < 0 || shareNum > 100) {
      toast.error('Share percentage must be between 0 and 100');
      return;
    }
    if (isActive && projectedTotal > 100) {
      toast.error(`Total partner shares cannot exceed 100% (currently ${projectedTotal.toFixed(2)}%)`);
      return;
    }

    setSubmitting(true);
    try {
      await upsertPartner.mutateAsync({
        id: partner?.id,
        name: name.trim(),
        phone: phone.trim() || null,
        share_percent: shareNum,
        is_active: isActive,
        notes: notes.trim() || null,
      });
      toast.success(partner ? 'Partner updated successfully' : 'Partner added successfully');
      onClose();
    } catch (err) {
      const parsed = parseRpcError(err);
      toast.error(parsed.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs" data-field-guide-scope>
      <div className="obsidian-panel relative w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl animate-in fade-in zoom-in-95">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-start justify-between gap-3 pr-8">
          <h2 className="text-lg font-semibold text-foreground">
            {partner ? 'Edit Partner' : 'Add New Partner'}
          </h2>
          <UserGuide compact />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Partners represent business equity holders and capital contributors
        </p>

        <form onSubmit={handleSubmit} noValidate className="mt-5 space-y-4">
          {/* Name */}
          <div className="space-y-1.5">
            <label htmlFor="p-name" className="text-xs font-medium text-foreground">
              Partner Name <span className="text-destructive">*</span>
            </label>
            <Input
              id="p-name"
              type="text"
              placeholder="e.g. Nandu, Vipi, Sreekutty"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputCls}
              disabled={submitting}
              helpLabel="Partner Name *"
            />
          </div>

          {/* Phone */}
          <div className="space-y-1.5">
            <label htmlFor="p-phone" className="text-xs font-medium text-foreground">
              Phone Number (Optional)
            </label>
            <Input
              id="p-phone"
              type="tel"
              placeholder="e.g. 9876543210"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={inputCls}
              disabled={submitting}
              helpLabel="Phone Number (Optional)"
            />
          </div>

          {/* Share Percent */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label htmlFor="p-share" className="text-xs font-medium text-foreground">
                Equity Share (%) <span className="text-destructive">*</span>
              </label>
              <span className={`text-xs ${projectedTotal > 100 ? 'text-destructive font-semibold' : 'text-muted-foreground'}`}>
                Total Allocated: {projectedTotal.toFixed(1)}% / 100%
              </span>
            </div>
            <div className="relative">
              <Input
                id="p-share"
                type="number"
                min="0"
                max="100"
                step="0.01"
                placeholder="0.00"
                value={sharePercent}
                onChange={(e) => setSharePercent(e.target.value)}
                className={`${inputCls} pr-8`}
                disabled={submitting}
                helpLabel="Equity share (%)"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
            </div>
          </div>

          {/* Status Active Toggle */}
          {partner && (
            <div className="flex items-center justify-between rounded-lg border border-border/80 bg-muted/20 p-3">
              <div>
                <p className="text-xs font-medium text-foreground">Active Status</p>
                <p className="text-[11px] text-muted-foreground">
                  Inactive partners cannot be selected for new capital contributions
                </p>
              </div>
              <label className="relative inline-flex cursor-pointer items-center">
                <Input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="peer sr-only"
                  disabled={submitting}
                  helpLabel="Active partner"
                />
                <div className="peer h-5 w-9 rounded-full bg-muted peer-checked:bg-primary peer-focus:outline-none after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full"></div>
              </label>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-1.5">
            <label htmlFor="p-notes" className="text-xs font-medium text-foreground">
              Notes (Optional)
            </label>
            <Input
              id="p-notes"
              type="text"
              placeholder="e.g. Managing Partner"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={inputCls}
              disabled={submitting}
              helpLabel="Notes (Optional)"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2.5 pt-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-md border border-border bg-background px-4 py-2 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || (isActive && projectedTotal > 100)}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {partner ? 'Save Changes' : 'Create Partner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
